import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';

import { ExternalLinkIcon, InfoIcon, ZapIcon } from '@/imports/ui/chakra/feather';

import { useGenerateImageAltText } from './useGenerateImageAltText';

const ALT_TEXT_MAX_LENGTH = 250;

export const ImageAlternativeText: React.FC<{
  alternativeText: string;
  onAlternativeTextChange: (value: string) => void;
  isDecorative: boolean;
  onIsDecorativeChange: (value: boolean) => void;
  imageProxy?: string | null;
}> = ({
  alternativeText,
  onAlternativeTextChange,
  isDecorative,
  onIsDecorativeChange,
  imageProxy,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const helpDisclosure = useDisclosure();
  const { generateAlternativeText, isGenerating } = useGenerateImageAltText();

  const onGenerateClick = async () => {
    if (!imageProxy) {
      return;
    }

    const generated = await generateAlternativeText(imageProxy);
    if (!generated) {
      return;
    }

    if (isDecorative) {
      onIsDecorativeChange(false);
    }
    onAlternativeTextChange(generated.slice(0, ALT_TEXT_MAX_LENGTH));
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  };

  return (
    <Stack spacing="4">
      <FormControl>
        <Stack spacing="2" mb="2">
          <HStack spacing="1">
            <FormLabel mb="0">Alternative text</FormLabel>
            <Tooltip
              label="Describe the image for people who use screen readers."
              placement="top"
              hasArrow
            >
              <Box display="inline-flex" color="gray.500" cursor="default">
                <InfoIcon boxSize="3.5" />
              </Box>
            </Tooltip>
          </HStack>

          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<ZapIcon boxSize="3.5" />}
            px="3"
            py="2"
            onClick={onGenerateClick}
            isLoading={isGenerating}
            isDisabled={!imageProxy}
            loadingText="Generating"
            alignSelf="flex-start"
          >
            Generate with AI
          </Button>
        </Stack>

        <Textarea
          ref={textareaRef}
          value={alternativeText}
          onChange={(event) =>
            onAlternativeTextChange(
              event.target.value.slice(0, ALT_TEXT_MAX_LENGTH)
            )
          }
          placeholder="Describe the image's purpose or important information"
          isDisabled={isDecorative}
          resize="vertical"
          minH="24"
          maxLength={ALT_TEXT_MAX_LENGTH}
        />

        <Text color="gray.500" fontSize="sm" mt="2">
          Describe what someone needs to know if they cannot see the image.
          Avoid starting with “image of.”
        </Text>
        <Text color="gray.500" fontSize="sm" textAlign="right" mt="1">
          {alternativeText.length} / {ALT_TEXT_MAX_LENGTH}
        </Text>
      </FormControl>

      <Checkbox
        isChecked={isDecorative}
        onChange={(event) => {
          const nextIsDecorative = event.target.checked;
          onIsDecorativeChange(nextIsDecorative);

          if (!nextIsDecorative) {
            window.setTimeout(() => textareaRef.current?.focus(), 0);
          }
        }}
        alignItems="flex-start"
        colorScheme="blue"
        sx={{
          '.chakra-checkbox__control': {
            mt: '0.5',
          },
        }}
      >
        <Box>
          <Text fontWeight="semibold">Mark image as decorative</Text>
          <Text color="gray.500" fontSize="sm" fontWeight="normal" mt="1">
            Use this when the image is only visual decoration and does not add
            information. Screen readers will skip decorative images.
          </Text>
        </Box>
      </Checkbox>

      <Box>
        <Button
          variant="link"
          colorScheme="blue"
          fontWeight="medium"
          fontSize="sm"
          rightIcon={<ExternalLinkIcon boxSize="3.5" />}
          onClick={helpDisclosure.onToggle}
        >
          What is alternative text?
        </Button>

        <Collapse in={helpDisclosure.isOpen} animateOpacity>
          <Box
            mt="3"
            p="4"
            border="1px"
            borderColor="gray.200"
            borderRadius="lg"
            bg="gray.50"
          >
            <Text fontWeight="semibold" mb="2">
              What is alternative text?
            </Text>
            <Text fontSize="sm" mb="3">
              Alternative text explains the important information or purpose of
              an image for people who use screen readers.
            </Text>
            <Text fontWeight="semibold" fontSize="sm" mb="1">
              Good:
            </Text>
            <Text fontSize="sm" fontStyle="italic" color="green.600" mb="3">
              “Three farmers walking through a corn field at sunset.”
            </Text>
            <Text fontWeight="semibold" fontSize="sm" mb="1">
              Avoid:
            </Text>
            <Text fontSize="sm" fontStyle="italic" color="red.500">
              “Image of farmers” or a file name like “IMG_1234.jpg.”
            </Text>
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
};
